import React, { useState, useEffect } from 'react';
import { X, Star, Mail, Phone, FileText, ExternalLink, Loader } from 'lucide-react';

export default function CandidateComparisonModal({ applicationIds, onClose }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchComparison = async () => {
      try {
        const response = await fetch('https://tradelink-kseg.onrender.com/api/candidate-comparison/compare', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ applicationIds })
        });

        if (response.ok) {
          const data = await response.json();
          setCandidates(data.candidates);
        } else {
          setError('Failed to fetch candidate data');
        }
      } catch (err) {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };

    fetchComparison();
  }, [applicationIds]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-8 flex items-center gap-3">
          <Loader className="animate-spin text-blue-600" size={32} />
          <span className="text-lg font-medium">Loading candidates...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-8 max-w-md">
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <button onClick={onClose} className="w-full bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl my-8">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-800">Compare Candidates ({candidates.length})</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={28} />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10">
                  Field
                </th>
                {candidates.map((candidate, idx) => (
                  <th key={idx} className="px-4 py-3 text-left text-sm font-semibold text-gray-700 min-w-[300px]">
                    Candidate {idx + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Name */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Name</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    <div className="font-semibold text-gray-900">{c.applicant_name}</div>
                  </td>
                ))}
              </tr>

              {/* Status */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Status</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(c.status)}`}>
                      {c.status}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Rating */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Rating</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    {c.rating_info ? (
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star
                              key={star}
                              size={18}
                              className={star <= c.rating_info.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                            />
                          ))}
                          <span className="ml-2 text-sm text-gray-600">({c.rating_info.rating}/5)</span>
                        </div>
                        {c.rating_info.comment && (
                          <p className="text-sm text-gray-600 italic">"{c.rating_info.comment}"</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Not rated</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Contact */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Contact</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-gray-400" />
                        <a href={`mailto:${c.applicant_email}`} className="text-blue-600 hover:underline">
                          {c.applicant_email}
                        </a>
                      </div>
                      {c.applicant_phone && (
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-gray-400" />
                          <span>{c.applicant_phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Tags */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Tags</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    {c.tags && c.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {c.tags.map((tag, tidx) => (
                          <span
                            key={tidx}
                            className={`px-2 py-1 rounded-full text-xs bg-${tag.color}-100 text-${tag.color}-700`}
                          >
                            {tag.tag_name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">No tags</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Notes */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Notes</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    <span className="text-sm text-gray-600">{c.notes_count} note(s)</span>
                  </td>
                ))}
              </tr>

              {/* Documents */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Documents</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    <div className="space-y-2">
                      {c.cv_url && (
                        <a href={c.cv_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                          <FileText size={14} />
                          <span>CV</span>
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {c.portfolio_url && (
                        <a href={c.portfolio_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                          <ExternalLink size={14} />
                          <span>Portfolio</span>
                        </a>
                      )}
                      {c.linkedin_url && (
                        <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                          <ExternalLink size={14} />
                          <span>LinkedIn</span>
                        </a>
                      )}
                      {c.github_url && (
                        <a href={c.github_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline">
                          <ExternalLink size={14} />
                          <span>GitHub</span>
                        </a>
                      )}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Cover Letter */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Cover Letter</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    {c.cover_letter ? (
                      <p className="text-sm text-gray-700 line-clamp-4">{c.cover_letter}</p>
                    ) : (
                      <span className="text-gray-400 text-sm">No cover letter</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Application Date */}
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-4 text-sm font-medium text-gray-700 sticky left-0 bg-white">Applied On</td>
                {candidates.map((c, idx) => (
                  <td key={idx} className="px-4 py-4">
                    <span className="text-sm text-gray-600">
                      {new Date(c.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition font-medium"
          >
            Close Comparison
          </button>
        </div>
      </div>
    </div>
  );
}
